import { Alert, AlertDescription, AlertTitle } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { shouldShowLabelNotPrintedWarning } from "@client-shared/lib/labelPrinting";
import type { PurchaseOrder, PurchaseOrderStatus } from "@shared/types";
import { AlertTriangle, Loader2, RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ERROR_NOTICE_STATUSES } from "../constants";
import { collectOrderErrors, setOrderDetailNoticeTestId } from "./order-detail-notice.utils";

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const ERROR_ALERT_CLASS =
  "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200 [&>svg]:text-red-600 dark:[&>svg]:text-red-400";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AddressValidationAlert({ errors, testId }: Readonly<{ errors: string[]; testId: string }>) {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive" className={ERROR_ALERT_CLASS} testId={testId}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{t("orders.orderDetail.notices.addressValidationError.title")}</AlertTitle>
      <AlertDescription className="text-red-700 dark:text-red-300">
        <p>{t("orders.orderDetail.notices.addressValidationError.message")}</p>
        {errors.length > 0 && (
          <ul className="mt-1 list-disc pl-4">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}

function LabelErrorAlert({
  errors,
  orderStatus,
  onRetry,
  isRetryLoading,
  isRetryHidden,
  testId,
}: Readonly<{
  errors: string[];
  orderStatus: PurchaseOrderStatus;
  onRetry?: () => void;
  isRetryLoading?: boolean;
  isRetryHidden?: boolean;
  testId: string;
}>) {
  const { t } = useTranslation();
  const retryButton = (
    <Button
      size="sm"
      variant="outline"
      className="mb-2"
      onClick={onRetry}
      disabled={isRetryLoading}
      testId={setOrderDetailNoticeTestId("retryButton")}
    >
      {isRetryLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCcw className="mr-2 h-3 w-3" />}
      {t("orders.orderDetail.retry")}
    </Button>
  );
  return (
    <Alert variant="destructive" className={ERROR_ALERT_CLASS} testId={testId}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{t("orders.orderDetail.notices.labelError.title")}</AlertTitle>
      <AlertDescription className="text-red-700 dark:text-red-300">
        {orderStatus === "LabelError" && onRetry && !isRetryHidden && retryButton}
        <p>{t("orders.orderDetail.notices.labelError.message")}</p>
        {errors.length > 0 && (
          <ul className="mt-1 list-disc pl-4">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}

function LabelNotPrintedAlert({ testId }: Readonly<{ testId: string }>) {
  const { t } = useTranslation();
  return (
    <Alert testId={testId}>
      <AlertTitle>{t("orders.orderDetail.notices.labelNotPrinted.title")}</AlertTitle>
      <AlertDescription>{t("orders.orderDetail.notices.labelNotPrinted.message")}</AlertDescription>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Main component — dispatcher
// ---------------------------------------------------------------------------

interface OrderDetailNoticeProps {
  purchaseOrder: PurchaseOrder;
  onRetry?: () => void;
  isRetryLoading?: boolean;
  isRetryHidden?: boolean;
  testId?: string;
}

export function OrderDetailNotice({ purchaseOrder, onRetry, isRetryLoading, isRetryHidden, testId }: Readonly<OrderDetailNoticeProps>) {
  const { orderStatus, errorMessage, lineItems = [] } = purchaseOrder;
  const rootTestId = testId ?? setOrderDetailNoticeTestId("container");

  if (orderStatus === "AddressValidationError") {
    const errors = collectOrderErrors(errorMessage, lineItems);
    return <AddressValidationAlert errors={errors} testId={rootTestId} />;
  }

  if (orderStatus && ERROR_NOTICE_STATUSES.has(orderStatus)) {
    const errors = collectOrderErrors(errorMessage, lineItems);
    return (
      <LabelErrorAlert
        errors={errors}
        orderStatus={orderStatus}
        onRetry={onRetry}
        isRetryLoading={isRetryLoading}
        isRetryHidden={isRetryHidden}
        testId={rootTestId}
      />
    );
  }

  if (shouldShowLabelNotPrintedWarning(purchaseOrder)) {
    return <LabelNotPrintedAlert testId={rootTestId} />;
  }

  return null;
}
